// views/Profile/components/WalletContentTabs.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, BarChart3, ArrowLeftRight, Coins, Crown } from 'lucide-react';
import './WalletTabs.css';

const TAB_DEFS = [
  { key: 'summary', i18nKey: 'walletPage.contentTabs.summary', Icon: LayoutGrid },
  // El plan era su propia entrada de menú ("Subscriptions"), a dos sitios de
  // distancia del saldo que consume. Cuatro palabras para dinero en un menú de
  // siete entradas; ahora es una pestaña donde el dinero ya estaba.
  { key: 'plan', i18nKey: 'walletPage.contentTabs.plan', Icon: Crown },
  { key: 'coins', i18nKey: 'walletPage.actions.getMore', Icon: Coins },
  { key: 'usage', i18nKey: 'walletPage.contentTabs.usage', Icon: BarChart3 },
  {
    key: 'transactions',
    i18nKey: 'walletPage.contentTabs.transactions',
    Icon: ArrowLeftRight,
  },
];

const FALLBACK = {
  summary: 'Summary',
  plan: 'Plan',
  usage: 'Usage',
  transactions: 'Transactions',
  coins: 'Get more coins',
};

/**
 * WalletContentTabs — segmented control that splits the account's billing
 * into focused dashboards: Summary (balance + plan + usage cards), Plan (the
 * contracted tier), Coins (the package picker), Usage (full per-resource
 * breakdown) and Transactions (the ledger).
 *
 * La pestaña vive en la URL (?tab=) y la controla quien monta la vista, para que
 * recargar no vuelva a la primera y para que un enlace a una concreta funcione.
 * Coins además necesita ser una URL real: Tilopay redirige de vuelta a ella.
 *
 * Props:
 *   activeTab: 'summary' | 'plan' | 'coins' | 'usage' | 'transactions'
 *   onChange:  (tab) => void
 */
export default function WalletContentTabs({ activeTab, onChange }) {
  const { t } = useTranslation();

  return (
    <div
      className="wallet-tabs wallet-tabs--content"
      role="tablist"
      aria-label="Wallet section"
    >
      {TAB_DEFS.map(({ key, i18nKey, Icon }) => {
        const selected = activeTab === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            id={`wallet-content-tab-${key}`}
            aria-selected={selected}
            aria-controls={`wallet-content-panel-${key}`}
            tabIndex={selected ? 0 : -1}
            className={`wallet-tabs__tab${
              selected ? ' wallet-tabs__tab--selected' : ''
            }`}
            onClick={() => onChange?.(key)}
          >
            <Icon size={14} aria-hidden="true" />
            {t(i18nKey, FALLBACK[key])}
          </button>
        );
      })}
    </div>
  );
}
